import type { ReservationUpdate } from '@/components/modal/ReservationChange.vue';
import { isPastUnconfirmed } from '@/lib/services/reservation';
import { BlockingMode, type MaintenanceTimeSpan } from '@/lib/types/maintenance';
import { ReservationStatus, type Reservation } from '@/lib/types/reservation';
import { UserAuthorizations } from '@/lib/types/user';
import { getErrorMessage } from '@/plugins/i18n/enums';
import { useAuthStore } from '@/store/auth';
import { useMaintenancesStore } from '@/store/maintenance';
import { useReservationsStore } from '@/store/reservation';
import { useResourcesStore } from '@/store/resource';
import type { CalendarFilters } from '@/types/calendarFilters';
import { createDefaultCalendarFilters, ReservationType } from '@/types/calendarFilters';
import { formatDateToyyyMMdd, msToResourceLocalWallClockDate, parseDateTime } from '@/utils/date';
import type { EventSourceInput } from '@fullcalendar/core';
import { useToast } from 'primevue/usetoast';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export function useCalendarPage() {
    const reservationsStore = useReservationsStore();
    const resourcesStore = useResourcesStore();
    const maintenancesStore = useMaintenancesStore();
    const authStore = useAuthStore();
    const toast = useToast();
    const { t } = useI18n();
    const slotConflictError = ref<string | null>(null);

    // ===== FILTER STATE =====
    const showFilterDialog = ref(false);
    const filters = ref<CalendarFilters>(createDefaultCalendarFilters());

    const selectedResourceTypes = computed(() => {
        return resourcesStore.types.filter(rt => filters.value.resourceTypeIds.includes(rt.id));
    });

    const selectedResources = computed(() => {
        return resourcesStore.resources.filter(r => filters.value.resourceIds.includes(r.id));
    });

    const filteredResources = computed(() => {
        if (filters.value.resourceTypeIds.length === 0) {
            return resourcesStore.resources;
        }
        return resourcesStore.resources.filter(r =>
            filters.value.resourceTypeIds.includes(r.type.id)
        );
    });

    const hasActiveFilters = computed(() =>
        filters.value.reservationType !== null ||
        filters.value.resourceTypeIds.length > 0 ||
        filters.value.resourceIds.length > 0 ||
        filters.value.visitorTypes.length > 0 ||
        filters.value.showOnlyMyReservations
    );

    const activeFiltersCount = computed(() => {
        let count = 0;
        if (filters.value.reservationType !== null) count++;
        if (filters.value.resourceTypeIds.length > 0) count++;
        if (filters.value.resourceIds.length > 0) count++;
        if (filters.value.visitorTypes.length > 0) count++;
        if (filters.value.showOnlyMyReservations) count++;
        return count;
    });

    function clearFilters() {
        filters.value = createDefaultCalendarFilters();
    }

    // ===== RESERVATION DETAIL MODAL =====
    const selectedReservation = ref<Reservation | null>(null);
    const selectedResourceId = ref<string | null>(null);
    const isModalOpen = ref(false);

    function closeModal() {
        isModalOpen.value = false;
        selectedReservation.value = null;
        selectedResourceId.value = null;
    }

    // ===== CREATE RESERVATION MODAL =====
    const createModalOpen = ref(false);
    const selectedStartMs = ref<number | null>(null);
    const selectedEndMs = ref<number | null>(null);

    function openCreateModal(startMs: number, endMs: number) {
        selectedStartMs.value = startMs;
        selectedEndMs.value = endMs;
        createModalOpen.value = true;
    }

    function closeCreateModal() {
        createModalOpen.value = false;
        selectedStartMs.value = null;
        selectedEndMs.value = null;
    }

    // ===== CHANGE RESERVATION MODAL =====
    const pendingChange = ref<ReservationUpdate | null>(null);
    const pendingReservation = ref<Reservation | null>(null);
    const pendingResourceName = ref('');
    let pendingRevert: (() => void) | null = null;

    const isChangeModalOpen = computed(() => !!pendingChange.value);

    function openChangeModal(
        change: ReservationUpdate,
        reservation: Reservation,
        resourceName: string,
        revertFn: () => void
    ) {
        pendingChange.value = change;
        pendingReservation.value = reservation;
        pendingResourceName.value = resourceName;
        pendingRevert = revertFn;
    }

    function clearPending() {
        pendingChange.value = null;
        pendingReservation.value = null;
        pendingResourceName.value = '';
        pendingRevert = null;
    }

    async function confirmChange() {
        const change = pendingChange.value;
        const reservation = pendingReservation.value;
        if (!change || !reservation) return;

        const updatedSlots = reservation.slots.map(s =>
            s.resourceId === change.resourceId
                ? { ...s, startMs: change.newStart, endMs: change.newEnd }
                : s
        );

        const updatedReservation = {
            ...reservation,
            slots: updatedSlots
        };

        const errorValidate = await reservationsStore.validateOne(updatedReservation);
        if (errorValidate) {
            toast.add({
                severity: 'error',
                summary: t('error.title'),
                detail: getErrorMessage(errorValidate, t),
                life: 5000
            });
            if (pendingRevert) pendingRevert();
            clearPending();
            return;
        }

        const errorUpdate = await reservationsStore.updateOne(updatedReservation);
        if (errorUpdate) {
            toast.add({
                severity: 'error',
                summary: t('error.title'),
                detail: getErrorMessage(errorUpdate, t),
                life: 5000
            });
        } else {
            toast.add({
                severity: 'success',
                summary: t('common.success'),
                detail: t('calendar.changeConfirm.updateSuccess'),
                life: 3000
            });
            clearPending();
        }
    }

    function cancelChange() {
        if (pendingRevert) pendingRevert();
        clearPending();
    }

    // ===== HELPER FUNCTIONS =====
    function getResourceNameById(id: string): string {
        const resource = resourcesStore.getResourceById(id);
        return resource?.name || id;
    }

    function findReservationAndSlot(reservationId: string, resourceId: string) {
        const reservation = reservationsStore.reservations.find(r => r.id === reservationId) || null;
        const slot = reservation?.slots.find(s => s.resourceId === resourceId) || null;
        return { reservation, slot };
    }

    function canModifyReservation(reservation: Reservation): boolean {
        const userPermissions = authStore.user?.permissions || [];
        const currentUserId = authStore.user?.id;

        const canManageAll = userPermissions.includes(UserAuthorizations.ManageAllReservations);
        const isOwner = reservation.createdBy?.id === currentUserId;

        if (!canManageAll && !isOwner) {
            return false;
        }

        const isExternal = reservation.visitors && reservation.visitors.length > 0;

        if (isExternal) {
            return userPermissions.includes(UserAuthorizations.ManageOwnReservations);
        } else {
            return userPermissions.includes(UserAuthorizations.ManageInternalReservations);
        }
    }

    // ===== EVENT MAPPING =====

    const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
    const DEFAULT_RESOURCE_COLOR = '#1b1b7f';

    /**
     * Single validation boundary for administrator-supplied resource colours.
     *
     * The raw value is free text from MySQL and previously reached the CSSOM unchecked as the inline custom
     * property --preview-accent on an element teleported to <body> at z-index 10000. Browsers refuse to let a
     * var() substitution split into a second declaration, so this was not a proven injection — but browser
     * leniency was the only thing standing in the way. It also fed hexToTintedBg (parseInt -> NaN ->
     * "rgba(NaN, NaN, NaN, 0.3)") and darkenColor (-> the literal "#NaNNaNNaN"), both silently discarded, so
     * malformed colours already degraded with no signal. Normalising once here fixes all three at the source.
     */
    function normalizeResourceColor(raw: string | null | undefined): string {
        const candidate = raw ? `#${raw.replace('#', '')}` : '';
        return HEX_COLOR.test(candidate) ? candidate : DEFAULT_RESOURCE_COLOR;
    }

    function hexToTintedBg(hex: string, opacity: number): string {
        const clean = hex.replace('#', '');
        const r = parseInt(clean.substring(0, 2), 16);
        const g = parseInt(clean.substring(2, 4), 16);
        const b = parseInt(clean.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    function mapReservationToEvents(reservation: Reservation): any[] {
        const events: any[] = [];
        if (!reservation.slots) return events;

        // Computed ONCE per reservation — eventContent only reads the precomputed flag (never recomputes eligibility)
        const overdue = isPastUnconfirmed(reservation, resourcesStore.getResourceById);

        // Hover-preview payload (HOVR-01): reservation-scoped values, computed once per reservation like `overdue` above.
        // Absent values are null (never '' or a placeholder) so the card omits the whole row instead of rendering it empty.
        const ownerName = reservation.createdBy ? `${reservation.createdBy.firstName} ${reservation.createdBy.lastName}`.trim() || null : null;
        const firstVisitor = reservation.visitors && reservation.visitors.length > 0 ? reservation.visitors[0] : null;
        const visitorName = firstVisitor ? `${firstVisitor.firstName} ${firstVisitor.lastName}`.trim() || null : null;
        const otherResourceCount = reservation.slots.length - 1;

        reservation.slots.forEach((slot) => {
            const resource = resourcesStore.getResourceById(slot.resourceId);
            const resourceName = resourcesStore.getResourceDisplayNameById(slot.resourceId) || '';
            const resourceColor = normalizeResourceColor(resource?.color);

            let bgOpacity = 0.2;
            switch (reservation.status) {
                case ReservationStatus.Completed:
                    bgOpacity = 0.7;
                    break;
                case ReservationStatus.Canceled:
                    bgOpacity = 0.2;
                    break;
                case ReservationStatus.Planned:
                    bgOpacity = 0.3;
                    break;
            }

            const tintedBg = hexToTintedBg(resourceColor, bgOpacity);
            const textColor = darkenColor(resourceColor, 40);
            const classNames: string[] = ['event-custom'];

            switch (reservation.status) {
                case ReservationStatus.Canceled:
                    classNames.push('event-canceled');
                    break;
                case ReservationStatus.Planned:
                    classNames.push('event-planned');
                    break;
                case ReservationStatus.Completed:
                    classNames.push('event-completed');
                    break;
            }

            if (overdue) {
                classNames.push('event-unconfirmed');
            }

            events.push({
                id: `${reservation.id}-${slot.resourceId}`,
                title: reservation.title,
                // THE DISPLAY SHIFT. FullCalendar reads every Date on the operator's browser clock, so it is handed
                // a Date whose wall clock IS the resource's wall clock — the event then lands on the resource's own
                // row. This value is a display fiction: the slot-instant pair on `extendedProps` below carries the TRUE
                // instants, and every consumer that computes, compares or persists must read those.
                start: msToResourceLocalWallClockDate(resource?.timezone?.id ?? null, slot.startMs),
                end: msToResourceLocalWallClockDate(resource?.timezone?.id ?? null, slot.endMs),
                backgroundColor: tintedBg,
                borderColor: 'transparent',
                textColor,
                editable: reservation.status === ReservationStatus.Planned,
                classNames,
                extendedProps: {
                    eventType: 'reservation',
                    reservationId: reservation.id,
                    resourceId: slot.resourceId,
                    isExternal: reservation.visitors && reservation.visitors.length > 0,
                    resourceName,
                    // TZ-03 / D-18: the resource's IANA zone id, read from the `resource` already resolved above so
                    // this costs no extra lookup. The chip marker and the hover preview card both read THIS field —
                    // one value, two surfaces, so they cannot disagree about whether the resource is on another clock.
                    // Absent is null (never '' or a placeholder) so the card omits the whole row rather than render it empty.
                    resourceTimezoneId: resource?.timezone?.id ?? null,
                    description: reservation.description || '',
                    status: reservation.status,
                    accentColor: resourceColor,
                    isPastUnconfirmed: overdue,
                    slotStartMs: slot.startMs,
                    slotEndMs: slot.endMs,
                    otherResourceCount,
                    ownerName,
                    visitorName,
                    attendees: reservation.attendees
                }
            });
        });

        return events;
    }

    function darkenColor(hex: string, percent: number): string {
        const cleanHex = hex.replace('#', '');
        const r = Math.max(0, parseInt(cleanHex.substring(0, 2), 16) - Math.round(255 * percent / 100));
        const g = Math.max(0, parseInt(cleanHex.substring(2, 4), 16) - Math.round(255 * percent / 100));
        const b = Math.max(0, parseInt(cleanHex.substring(4, 6), 16) - Math.round(255 * percent / 100));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }

    function mapMaintenanceToEvents(timeSpans: MaintenanceTimeSpan[]): any[] {
        return timeSpans.map((timeSpan, index) => {
            const resourceName = resourcesStore.getResourceDisplayNameById(timeSpan.resourceId) || '';
            const resource = resourcesStore.getResourceById(timeSpan.resourceId);
            const isPartial = timeSpan.blockingMode === BlockingMode.PartiallyBlocking;
            const resourceTimezoneId = resource?.timezone?.id ?? null;
            const trueStartMs = parseDateTime(timeSpan.startUtc).getTime();
            const trueEndMs = parseDateTime(timeSpan.endUtc).getTime();

            return {
                id: `maintenance-${timeSpan.resourceId}-${index}`,
                title: timeSpan.title,
                // THE DISPLAY SHIFT — same rule, same reason as the reservation branch above.
                start: msToResourceLocalWallClockDate(resourceTimezoneId, trueStartMs),
                end: msToResourceLocalWallClockDate(resourceTimezoneId, trueEndMs),
                // These three are written INLINE by FullCalendar onto the event element, which is why the D-11
                // `maintenance-partial` modifier differentiates through background-image and border-style instead:
                // a stylesheet rule cannot beat an inline style without !important, and that would break this pair.
                backgroundColor: 'rgba(245, 158, 11, 0.3)',
                borderColor: '#D97706',
                textColor: '#92400E',
                editable: false,
                // Regular block events on purpose — deliberately NOT FullCalendar background events, despite both
                // CLAUDE.md files claiming otherwise. Background events never fire eventMouseEnter, so switching
                // them over would silently kill the Phase 2 hover preview card. Do not "fix" this.
                classNames: isPartial ? ['event-custom', 'maintenance-event', 'maintenance-partial'] : ['event-custom', 'maintenance-event'],
                extendedProps: {
                    eventType: 'maintenance',
                    resourceId: timeSpan.resourceId,
                    resourceName: resourceName,
                    // TZ-03 / D-18: same field, same meaning as the reservation branch above. Both branches must carry
                    // it — the chip marker and the preview card read THIS field, so omitting it here would silently
                    // drop the marker and the resource-local row for every maintenance event. Absent is null.
                    resourceTimezoneId,
                    // The TRUE instants, under the same two names the reservation branch uses so buildPreviewModel
                    // reads one field name in both branches. The event's own start/end above are display-shifted and
                    // mean nothing as instants — these are the only true instants that survive into the component,
                    // and every consumer that computes, compares or persists must read THESE.
                    slotStartMs: trueStartMs,
                    slotEndMs: trueEndMs,
                    description: timeSpan.description || '',
                    reducedCapacity: timeSpan.reducedCapacity,
                    isMaintenance: true
                }
            };
        });
    }

    // ===== EVENT SOURCE =====
    const eventSourceFunc: EventSourceInput = async (fetchInfo, successCallback, failureCallback) => {
        try {
            const start = new Date(fetchInfo.start);
            const end = new Date(fetchInfo.end);

            // One calendar day of slack on each side of FullCalendar's own range. An event's DISPLAY
            // position can now sit up to a day away from its TRUE instant (the per-resource shift in
            // the two event builders above), so an event visible at the edge of the view may have a
            // true instant outside the range that was asked for — and it would silently not render.
            // This only ever WIDENS the window: FullCalendar clips to the view, so the extra events
            // cost a little bandwidth and change nothing on screen.
            start.setDate(start.getDate() - 1);
            end.setDate(end.getDate() + 1);

            const fromStr = formatDateToyyyMMdd(start);
            const toStr = formatDateToyyyMMdd(end);

            await reservationsStore.fetchByDates(
                fromStr,
                toStr,
                filters.value.reservationType === null ? undefined : filters.value.reservationType,
                selectedResourceTypes.value,
                selectedResources.value,
                filters.value.visitorTypes,
                filters.value.showOnlyMyReservations
            );

            const reservationEvents = reservationsStore.reservations.flatMap(r => mapReservationToEvents(r));

            let maintenanceEvents: any[] = [];
            if (filters.value.showMaintenance) {
                await maintenancesStore.fetchTimeSpans(fromStr, toStr);

                // Filter timespans based on selected resources and resource types
                const filteredTimeSpans = maintenancesStore.timespans.filter(ts => {
                    const matchesResourceType = selectedResourceTypes.value.length === 0 ||
                        selectedResourceTypes.value.some(rt => rt.id === resourcesStore.getResourceById(ts.resourceId)?.type.id);
                    const matchesResource = selectedResources.value.length === 0 ||
                        selectedResources.value.some(r => r.id === ts.resourceId);
                    return matchesResourceType && matchesResource;
                });

                maintenanceEvents = mapMaintenanceToEvents(filteredTimeSpans);
            }

            // Sort by start time, then by type (maintenance last)
            const allEvents = [...reservationEvents, ...maintenanceEvents].sort((a, b) => {
                const aStart = a.start?.getTime?.() ?? 0;
                const bStart = b.start?.getTime?.() ?? 0;
                if (aStart !== bStart) {
                    return aStart - bStart;
                }
                const aType = a.extendedProps?.eventType === 'maintenance' ? 1 : 0;
                const bType = b.extendedProps?.eventType === 'maintenance' ? 1 : 0;
                return aType - bType;
            });
            successCallback(allEvents);
        } catch (error) {
            console.error('Error fetching calendar events:', error);
            failureCallback(error as Error);
        }
    };

    // ===== EVENT HANDLERS =====
    function handleEventClick(reservationId: string, resourceId: string) {
        selectedReservation.value =
            reservationsStore.reservations.find(r => r.id === reservationId) || null;
        selectedResourceId.value = resourceId;
        isModalOpen.value = true;
    }

    function handleEventDrop(
        reservationId: string,
        resourceId: string,
        newStart: number,
        newEnd: number,
        revertFn: () => void
    ) {
        const { reservation, slot } = findReservationAndSlot(reservationId, resourceId);
        if (!reservation || !slot) {
            revertFn();
            return;
        }

        if (!canModifyReservation(reservation)) {
            revertFn();
            return;
        }

        openChangeModal(
            {
                reservationId,
                resourceId,
                oldStart: slot.startMs,
                oldEnd: slot.endMs,
                newStart,
                newEnd,
                type: 'drop'
            },
            reservation,
            getResourceNameById(resourceId),
            revertFn
        );
    }

    function handleEventResize(
        reservationId: string,
        resourceId: string,
        newStart: number,
        newEnd: number,
        revertFn: () => void
    ) {
        const { reservation, slot } = findReservationAndSlot(reservationId, resourceId);
        if (!reservation || !slot) {
            revertFn();
            return;
        }

        if (!canModifyReservation(reservation)) {
            revertFn();
            return;
        }

        openChangeModal(
            {
                reservationId,
                resourceId,
                oldStart: slot.startMs,
                oldEnd: slot.endMs,
                newStart,
                newEnd,
                type: 'resize'
            },
            reservation,
            getResourceNameById(resourceId),
            revertFn
        );
    }

    async function initialize() {
        await resourcesStore.fetchAll();
    }

    return {
        ReservationType,
        resourcesStore,
        showFilterDialog,
        filters,
        filteredResources,
        hasActiveFilters,
        activeFiltersCount,
        clearFilters,
        selectedReservation,
        selectedResourceId,
        isModalOpen,
        closeModal,
        createModalOpen,
        selectedStartMs,
        selectedEndMs,
        openCreateModal,
        closeCreateModal,
        pendingChange,
        pendingReservation,
        pendingResourceName,
        isChangeModalOpen,
        confirmChange,
        cancelChange,
        eventSourceFunc,
        handleEventClick,
        handleEventDrop,
        handleEventResize,
        initialize
    };
}
